import React, { useState, useCallback, useEffect } from 'react';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    CircularProgress,
    Container,
    Typography,
    Box,
    Chip
} from '@material-ui/core';
import { DeleteIcon, EditIcon, FilterIcon, CheckIcon } from '../../../icons';
import ActionIconButton from '../../../components/atom/ActionIconButton';
import TablePager from '../../../components/atom/TablePager';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import AdminHeader from '../../../components/atom/AdminHeader';
import { useHistory } from 'react-router';
import BatchListPageSidebar from './components/BatchListPageSidebar';
import { formatMoney, formatDate } from '../../../utils';
import { parseXlsxSheets } from '../../../utils';
import { exportToCsv } from '../../../excelExportHelpers';
import { detailedSearchFields } from './components/DetailedSearchBlock';

const initialDetailedSearchValues = {
    [detailedSearchFields.vendorId.key]: '',
    [detailedSearchFields.batchNo.key]: '',
    [detailedSearchFields.fromDate.key]: '',
    [detailedSearchFields.endDate.key]: '',
    [detailedSearchFields.invoiceNo.key]: '',
    [detailedSearchFields.paymentRef.key]: ''
};

export const EXPORT_BATCH_VARIANTS = {
    ACCOUNTS_PAYABLE: 'ACCOUNTS_PAYABLE',
    PAYMENT_VARIANCE: 'PAYMENT_VARIANCE'
};

const AppliedFilters = ({ filters, setFilters }) => {
    const filterEntries = Object.entries(filters).filter(([, value]) => value);
    if (filterEntries.length === 0) {
        return null;
    }
    return (
        <Box display="flex" alignItems="center" style={{ marginLeft: 20 }}>
            <Box style={{ marginRight: 10, display: 'flex', alignItems: 'center' }}>
                <FilterIcon style={{ verticalAlign: 'middle', marginRight: 5 }} />
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    Applied filters:
                </Typography>
            </Box>
            {filterEntries.map(([key, value]) => (
                <Chip
                    size="small"
                    key={key}
                    label={`${detailedSearchFields[key].label} : ${value}`}
                    style={{ marginRight: 5 }}
                    onDelete={() => {
                        setFilters(prevFilters => ({
                            ...prevFilters,
                            [key]: initialDetailedSearchValues[key] || ''
                        }));
                    }}
                />
            ))}
        </Box>
    );
};

function BatchListPage() {
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();

    const history = useHistory();

    const [filters, setFilters] = useState(initialDetailedSearchValues);

    // eslint-disable-next-line no-unused-vars
    const [data, loading, error, setData, reload] = useSwaggerClient('batches', 'listBatches', {
        page,
        pageSize,
        ...filters
    });

    // create a new batch
    const createBatch = useCallback(() => {
        doSwaggerCall('batches', 'createBatch', {})
            .then(data => {
                notifySuccess('New batch created!');
                history.push(`/admin/batches/update/${data.id}`);
            })
            .catch(err => {
                console.error(err);
                notifyError('Error during batch creation!');
            });
    }, [history, notifyError, notifySuccess]);

    const deleteBatch = useCallback(
        batchId => {
            doSwaggerCall('batches', 'deleteBatch', { batchId })
                .then(() => {
                    notifySuccess('Batch deleted!');
                    reload();
                })
                .catch(err => {
                    console.log(err);
                    notifyError('Cannot delete batch!');
                });
        },
        [reload, notifyError, notifySuccess]
    );

    const updateBatch = useCallback(
        batch => {
            const updatedBatches = [];
            for (let i = 0; i < data.batches.length; i++) {
                for (let j = 0; j < batch.length; j++) {
                    if (data.batches[i].batchNo === batch[j].batch_no) {
                        if (batch[j].batch_no && batch[j].payment_date && batch[j].payment_ref) {
                            updatedBatches.push({
                                id: data.batches[i].id,
                                payment_date: batch[j].payment_date,
                                payment_ref: batch[j].payment_ref
                            });
                        }
                    }
                }
            }

            const batchToUpdate = updatedBatches.map(batch => {
                return doSwaggerCall('batches', 'updateBatch', {
                    batchId: batch.id,
                    body: {
                        updateData: {
                            payment_date: batch.payment_date,
                            payment_ref: batch.payment_ref
                        }
                    }
                });
            });

            Promise.all(batchToUpdate)
                .then(() => {
                    notifySuccess('Batch updated successfully!');
                    reload();
                })
                .catch(err => {
                    console.log(err);
                    notifyError('Cannot update batch!');
                });
        },
        [data, reload, notifyError, notifySuccess]
    );

    useEffect(() => {
        if (loading) {
            return;
        }

        if (data?.batches?.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, data, loading, setPage]);

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }

    const showUpdatesOnModal = updates => {
        return (
            <>
                <Typography
                    variant="h2"
                    style={{ justifySelf: 'center', color: 'red', marginBottom: '10px' }}
                >
                    This action is irreversible!
                </Typography>
                <Table style={{ border: '2px solid black' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Batch No</TableCell>
                            <TableCell>Payment Date</TableCell>
                            <TableCell>Payment Ref</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {updates.map((update, i) => {
                            const missingField =
                                !update.batch_no || !update.payment_date || !update.payment_ref;
                            return (
                                <TableRow
                                    key={i}
                                    style={{
                                        border: missingField ? '2px solid red' : 'inherit'
                                    }}
                                >
                                    <TableCell>{update.batch_no}</TableCell>
                                    <TableCell>{update.payment_date}</TableCell>
                                    <TableCell>{update.payment_ref}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </>
        );
    };

    const batchSchema = {
        batch_no: {
            default: '',
            transform: v => String(v ?? '').trim(),
            required: true
        },
        payment_ref: {
            default: '',
            transform: v => String(v ?? '').trim(),
            required: true
        },
        payment_date: {
            type: 'date',
            default: '',
            format: 'YYYY-MM-DD',
            required: true
        }
    };

    const handleFile = ev => {
        const file = ev.target.files?.[0];
        if (!file) {
            return;
        }

        parseXlsxSheets(
            file,
            rows => {
                showModal(MODAL_TYPE.CONFIRMATION, {
                    title: 'Check your updates',
                    content: showUpdatesOnModal(rows),
                    confirm: () => updateBatch(rows)
                });
            },
            err => console.error('Error in file processing', err),
            { schema: batchSchema }
        );
        ev.target.value = '';
    };

    return (
        <Container className="activity-edit page" maxWidth="none">
            <Grid className="pageWithSidebar">
                <BatchListPageSidebar
                    setFilters={setFilters}
                    filters={filters}
                    reload={reload}
                    total={data?.total}
                />
                <Grid className="pageContent">
                    <Grid container>
                        <AdminHeader
                            title="Batches"
                            createLabel="Create batch"
                            onCreate={() => {
                                createBatch();
                            }}
                            importLabel="Import batches"
                            onImport={handleFile}
                            downloadSampleLabel="Download sample"
                            onDownloadSample={() =>
                                exportToCsv('sampleBatch.csv', [
                                    ['batch_no', 'payment_date', 'payment_ref'],
                                    ['BATCH-12345678-1234', '2025-01-01', '12345'],
                                    ['BATCH-87654321-4321', '2024-12-31', '54321']
                                ])
                            }
                            childrenBefore={true}
                        >
                            <AppliedFilters filters={filters} setFilters={setFilters} />
                        </AdminHeader>
                        <Grid item container xs={12} justifyContent="center">
                            {loading ? (
                                <CircularProgress style={{ margin: 50 }} />
                            ) : (
                                <>
                                    {data?.batches?.length === 0 ? (
                                        <div style={{ textAlign: 'center', marginTop: 50 }}>
                                            <h3>No batches found.</h3>
                                            <p>Check your filters and try again.</p>
                                        </div>
                                    ) : (
                                        <Table style={{ backgroundColor: 'white' }} size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Batch no</TableCell>
                                                    <TableCell>Vendor name</TableCell>
                                                    <TableCell>Batch date</TableCell>
                                                    <TableCell>Amount</TableCell>
                                                    <TableCell>Vendor reference</TableCell>
                                                    <TableCell>From date</TableCell>
                                                    <TableCell>Thru date</TableCell>
                                                    <TableCell>Breakage</TableCell>
                                                    <TableCell>Vouchers</TableCell>
                                                    <TableCell>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {data?.batches.map(batch => (
                                                    <TableRow key={batch.id}>
                                                        <TableCell>{batch.batchNo}</TableCell>
                                                        <TableCell>{batch.vendorName}</TableCell>
                                                        <TableCell>
                                                            {formatDate(batch.batchDate)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatMoney(batch.amount)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {batch.vendorReference}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatDate(batch?.from)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatDate(batch?.to)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {batch.isBreakage && <CheckIcon />}
                                                        </TableCell>
                                                        <TableCell>
                                                            {batch.bookingItemCount}
                                                        </TableCell>
                                                        <TableCell style={{ width: 100 }}>
                                                            <ActionIconButton
                                                                title="Edit"
                                                                icon={EditIcon}
                                                                to={`/admin/batches/update/${batch.id}`}
                                                            />
                                                            <ActionIconButton
                                                                title="Delete"
                                                                icon={DeleteIcon}
                                                                onClick={() => {
                                                                    showModal(
                                                                        MODAL_TYPE.CONFIRMATION,
                                                                        {
                                                                            title: `Are you sure you want to remove this batch? All items will be unbatched.`,
                                                                            content:
                                                                                'This action is irreversible.',
                                                                            confirm: () =>
                                                                                deleteBatch(
                                                                                    batch.id
                                                                                )
                                                                        }
                                                                    );
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </>
                            )}
                            <Grid container justifyContent="flex-end">
                                <TablePager
                                    total={data?.total}
                                    page={page}
                                    setPage={setPage}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    );
}

export default BatchListPage;
